// notif.view — lonceng 🔔 di topbar + panel notifikasi + lompat ke tiket.
import { esc } from '@domain/format';
import { currentUser } from '@core/session';
import { openModal } from '@ui/modal';
import { toast } from '@ui/toast';
import { goto, refreshCounts } from '@core/nav';
import { supabase } from '@core/supabase';
import { bunyikan, nadaSaya, setNadaSaya, NADA } from '@core/sound';
import type { Employee } from '@core/types';
import * as repo from './notif.repo';

/** Apakah notif `target` relevan utk user (RLS sudah membatasi cabang). */
function targetMatches(target: string, u: Employee): boolean {
  if (u.role === 'Owner' || u.role === 'Manager') return true;
  if (target === 'EMP:' + u.id) return true; // reminder pribadi (mis. ingat absen masuk) — selaras RLS 0040
  switch (u.role) {
    case 'SPV': return target === 'SPV' || target === 'CS' || target === 'FINANCE' || target.startsWith('TEK:'); // N1: +FINANCE (v18); TEK per-tim ditunda (hierarki SPV belum diport, RLS batasi cabang)
    case 'CS': return target === 'CS';
    case 'Admin Finance': return target === 'FINANCE';
    case 'Teknisi': return target === 'TEK:' + u.nama;
    default: return false;
  }
}

async function mine(): Promise<repo.NotifRow[]> {
  const u = currentUser(); if (!u) return [];
  return (await repo.listNotifs()).filter((n) => targetMatches(n.target, u));
}

/**
 * Buang notif TUGAS yang sudah dikerjakan: notif ber-`tahap` yang tiketnya kini sudah maju dari
 * status itu. Status semua tiket terkait diambil dalam satu query. Notif tanpa `tahap` (informasi)
 * atau tanpa tiket dibiarkan — hilangnya tetap lewat "dibaca" seperti biasa.
 */
async function aktif(rows: repo.NotifRow[]): Promise<repo.NotifRow[]> {
  const perluCek = rows.filter((n) => n.tahap && n.ticket_no).map((n) => n.ticket_no!);
  if (!perluCek.length) return rows;
  let st: Map<string, string>;
  try { st = await repo.ticketStatuses(perluCek); } catch { return rows; }  // gagal cek → jangan sembunyikan apa pun
  return rows.filter((n) => repo.notifOutstanding(n, n.ticket_no ? st.get(n.ticket_no) : undefined));
}

/** Pasang lonceng ke topbar (dipanggil setelah renderShell). */
export async function mountBell(host: HTMLElement): Promise<void> {
  host.innerHTML = `<button class="pill-btn" id="bellBtn" title="Notifikasi" style="position:relative">🔔<span id="bellBadge" style="display:none;position:absolute;top:-4px;right:-4px;background:var(--danger);color:#fff;font-size:10px;font-weight:700;min-width:16px;height:16px;line-height:16px;border-radius:8px;padding:0 3px"></span></button>`;
  host.querySelector('#bellBtn')?.addEventListener('click', () => void openPanel());
  await refreshBadge(host);
  // Notifikasi datang lewat DUA jalur, sengaja:
  //  1. REALTIME — server mendorong barisnya begitu tersimpan (1–2 detik). Ini jalur utama;
  //     penanyaan berkala 30 detik dulu membuat request sparepart terasa telat masuk.
  //  2. Penanyaan berkala 60 detik sebagai jaring — koneksi toko bisa putus, dan langganan
  //     realtime yang mati tanpa kabar akan membuat lonceng diam selamanya.
  // Keduanya didaftarkan SEKALI (guard) agar tak menumpuk bila shell dirakit ulang tanpa reload.
  langgananRealtime();
  if (!bellPoll) bellPoll = window.setInterval(refreshTopbarBell, 60000);
}

let bellPoll = 0;
let kanalNotif: { unsubscribe: () => void } | null = null;

/**
 * Langganan notifikasi masuk. Filter target dilakukan di sisi klien memakai `targetMatches` yang
 * SAMA dengan daftar panel — RLS sudah menyaring di server, ini menghindari berbunyi untuk baris
 * yang kebetulan terlihat tapi bukan tugas orang ini (mis. Owner yang melihat semua).
 */
/**
 * Layak DIBUNYIKAN? Bukan sama dengan "layak dilihat".
 *
 * Owner & Manager boleh MELIHAT notifikasi seluruh perusahaan, dan itu benar untuk badge. Tapi
 * membunyikan bel untuk semuanya berarti: ±30 servis/cabang/hari × 5–8 notifikasi per tiket ≈ satu
 * bunyi tiap 2–3 menit untuk SATU cabang — dan rencananya sampai 10 cabang. Owner akan memilih
 * 🔇 di hari pertama, lalu eskalasi SLA yang justru ditujukan kepadanya tak pernah terdengar lagi.
 * Jadi yang berbunyi hanya yang benar-benar tugasnya sendiri.
 */
function layakBunyi(target: string, u: Employee): boolean {
  if (target === 'EMP:' + u.id) return true;                 // ditujukan ke orang ini
  if (isMgrRole(u.role)) return target === 'SPV';            // manajerial: hanya yang butuh keputusannya
  return targetMatches(target, u);                           // staf: memang hanya tugasnya sendiri
}

let segarTimer = 0;
/** Satu gelombang notifikasi bisa berisi belasan baris; jangan menyegarkan belasan kali. */
function segarkanTertunda(): void {
  window.clearTimeout(segarTimer);
  segarTimer = window.setTimeout(() => {
    refreshTopbarBell();
    refreshCounts();   // badge 💬 per-menu (mis. 📥 Request Masuk) — kalau tidak, bel berbunyi tapi angkanya masih lama
  }, 1200);
}

function langgananRealtime(): void {
  if (kanalNotif) return;
  try {
    kanalNotif = supabase()
      .channel('notif-masuk')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications' }, (pesan: { new?: { target?: string } }) => {
        const u = currentUser();
        const target = pesan.new?.target ?? '';
        if (!u || !target || !targetMatches(target, u)) return;
        if (layakBunyi(target, u)) bunyikan();
        segarkanTertunda();
      })
      .subscribe((status: string) => {
        // Kalau langganan berakhir gagal (Realtime mati, tabel belum masuk publication, jaringan
        // putus), guard di atas akan memblokir percobaan ulang SELAMANYA dan lonceng diam-diam
        // kembali bergantung pada jaring 60 detik — persis keluhan "telat masuk" yang mau dihapus.
        if (status !== 'SUBSCRIBED') kanalNotif = null;
      }) as unknown as { unsubscribe: () => void };
  } catch { /* realtime gagal → jaring 60 detik tetap bekerja */ }
}
// Saat tab kembali terlihat → segarkan badge. Didaftar sekali di module-scope (bukan tiap mountBell)
// supaya tak ada listener ganda; refreshTopbarBell aman no-op bila #bellHost belum ada / belum login.
document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshTopbarBell(); });

// Segregasi (permintaan Apo): O/M/SPV pisahkan "Tugas Saya" (managerial: target SPV = assign/SLA/approval)
// dari "Cabang" (pantauan scope: CS/FINANCE/TEK). Badge lonceng = HANYA managerial (tak terkubur). Teknisi/CS/Finance = 1 daftar.
const MGR_ROLES = ['Owner', 'Manager', 'SPV'];
const isMgrRole = (role: string): boolean => MGR_ROLES.includes(role);
const isManagerial = (target: string, empId: number): boolean => target === 'SPV' || target === 'EMP:' + empId;

async function refreshBadge(host: HTMLElement): Promise<void> {
  try {
    // Badge = TOTAL unread yang terlihat user (bukan cuma managerial) → dot muncul untuk aktivitas apa pun.
    // O/M: mine() = semua (targetMatches true); SPV: scope-nya; +managerial (SPV/EMP) yang bisa terlewat di cap mine().
    const u = currentUser();
    const rows = await mine();
    if (u && isMgrRole(u.role)) {
      const seen = new Set(rows.map((n) => n.id));
      for (const m of await repo.listManagerial(u.id)) if (!seen.has(m.id)) rows.push(m);
    }
    const unread = (await aktif(rows)).filter((n) => !n.read).length;
    const badge = host.querySelector('#bellBadge') as HTMLElement | null;
    if (badge) { badge.textContent = unread > 99 ? '99+' : String(unread); badge.style.display = unread ? '' : 'none'; }
  } catch { /* diam */ }
}

/**
 * Satu baris notifikasi. Dua hal yang diperbaiki dari versi awal:
 * 1) Yang bisa dibuka dirender sebagai <button>, bukan <div> — sebelumnya notifikasi
 *    TAK BISA dibuka lewat keyboard sama sekali (tak bisa di-Tab, tak bisa Enter).
 * 2) Status belum-dibaca tak lagi hanya beda warna latar: ada titik ● + teks tebal,
 *    supaya terbaca juga oleh yang sulit membedakan warna dan oleh pembaca layar.
 */
const itemHTML = (n: repo.NotifRow): string => {
  const bisaBuka = !!n.ticket_no;
  const merah = /refund|pengembalian dana/i.test(n.text);
  // <span display:block>, bukan <div>: <div> = flow content, tak sah di dalam <button> menurut spec.
  const isi = `<span style="display:block;font-size:13px${merah ? ';color:var(--danger)' : ''}${n.read ? '' : ';font-weight:600'}">${n.read ? '' : '<span aria-hidden="true" style="color:var(--blue)">●</span> '}${esc(n.text)}</span>
    <span style="display:block;font-size:11px;color:var(--muted);margin-top:2px">${esc(n.created_at.slice(0, 16).replace('T', ' '))}${bisaBuka ? ` · ${esc(n.ticket_no ?? '')} →` : ''}</span>`;
  // color:var(--txt) WAJIB — <button> tak mewarisi warna teks (UA default gelap) → di dark-mode
  // teks jadi hitam di latar gelap (tak terbaca). Latar pakai token tema, bukan #fff hardcoded.
  const gaya = `padding:8px 10px;border:0;border-bottom:1px solid var(--line);background:${n.read ? 'var(--surface)' : 'var(--ice)'};color:var(--txt);display:block;width:100%;text-align:left;font:inherit`;
  return bisaBuka
    ? `<button type="button" class="notif-item" data-no="${esc(n.ticket_no ?? '')}" data-id="${n.id}" aria-label="${n.read ? '' : 'Belum dibaca. '}${esc(n.text)} — buka servis ${esc(n.ticket_no ?? '')}" style="${gaya};cursor:pointer">${isi}</button>`
    : `<div class="notif-item" data-id="${n.id}" style="${gaya}">${n.read ? '' : '<span class="sr-only">Belum dibaca.</span>'}${isi}</div>`;
};
const listHTML = (rows: repo.NotifRow[]): string =>
  rows.length ? rows.map(itemHTML).join('') : '<div class="empty" style="padding:20px">Belum ada notifikasi.</div>';

async function openPanel(): Promise<void> {
  const u = currentUser();
  const mgrView = !!u && isMgrRole(u.role);
  let mgrList: repo.NotifRow[] = [], scopeList: repo.NotifRow[] = [], list: repo.NotifRow[];
  try {
    if (mgrView) {
      mgrList = await aktif(await repo.listManagerial(u!.id)); // fetch terpisah → managerial tak terkubur (review F1)
      scopeList = (await aktif(await mine())).filter((n) => !isManagerial(n.target, u!.id));
      const byId = new Map<number, repo.NotifRow>(); [...mgrList, ...scopeList].forEach((n) => byId.set(n.id, n));
      list = [...byId.values()]; // gabung unik (utk "tandai semua")
    } else { list = await aktif(await mine()); }
  } catch (e) { toast((e as Error).message); return; }
  const uc = (rows: repo.NotifRow[]): number => rows.filter((n) => !n.read).length;
  const startScope = mgrView && mgrList.length === 0 && scopeList.length > 0; // F3: buka ke tab yg ada isinya

  const tabBar = mgrView
    ? `<div class="nt-tabs"><button data-nttab="mgr" class="${startScope ? '' : 'active'}">🎯 Tugas Saya${uc(mgrList) ? ` <span class="pill" style="background:var(--dangerbg);color:var(--danger)">${uc(mgrList)}</span>` : ''}</button><button data-nttab="scope" class="${startScope ? 'active' : ''}">🏢 Cabang${uc(scopeList) ? ` <span class="pill" style="background:var(--ice);color:var(--navy)">${uc(scopeList)}</span>` : ''}</button></div>`
    : '';
  const firstPane = mgrView ? (startScope ? scopeList : mgrList) : list;
  const bg = openModal(`<div class="modal-head"><h3>🔔 Notifikasi</h3><button class="x">×</button></div>
    <div class="modal-body" style="padding:0">${tabBar}<div id="nt_pane" style="max-height:56vh;overflow:auto">${listHTML(firstPane)}</div></div>
    <div class="nt-nada">
      <label for="nt_nada">🔊 Bunyi saat ada yang masuk</label>
      <select id="nt_nada">${NADA.map((n) => `<option value="${n.id}" ${nadaSaya() === n.id ? 'selected' : ''}>${esc(n.label)}</option>`).join('')}</select>
      <button class="btn" id="nt_coba" style="padding:3px 10px">Coba</button>
      <div class="nt-nada-note">Bunyi aktif setelah Anda menyentuh halaman; HP dalam mode senyap tidak berbunyi, dan aplikasi yang tertutup di HP belum berbunyi.</div>
    </div>
    <div class="modal-foot"><button class="btn" id="nt_close">Tutup</button>${list.some((n) => !n.read) ? `<button class="btn primary" id="nt_read">Tandai semua dibaca</button>` : ''}</div>`);
  const pane = bg.querySelector('#nt_pane') as HTMLElement;

  const wirePane = (): void => {
    pane.querySelectorAll<HTMLElement>('.notif-item[data-no]').forEach((el) => {
      const no = el.dataset.no; if (!no) return;
      el.addEventListener('click', async () => {
        const id = +(el.dataset.id ?? 0); if (id) { try { await repo.markRead([id]); } catch { /* diam */ } }
        bg.remove(); refreshTopbarBell();
        try { const st = await repo.ticketStatus(no); if (st) goto('st_' + st.replace(/\s+/g, '_')); else toast(`Buka tiket ${no} lewat menu antrian.`); }
        catch { toast(`Buka tiket ${no} lewat menu antrian.`); }
      });
    });
  };
  wirePane();
  bg.querySelectorAll<HTMLElement>('button[data-nttab]').forEach((b) => b.addEventListener('click', () => {
    bg.querySelectorAll('button[data-nttab]').forEach((x) => x.classList.remove('active')); b.classList.add('active');
    pane.innerHTML = listHTML(b.dataset.nttab === 'scope' ? scopeList : mgrList); wirePane();
  }));
  // Pilihan nada diletakkan DI SINI, bukan di menu Pengaturan: yang mengeluh kebisingan atau tak
  // mendengar bunyi sedang menatap lonceng, dan pilihannya milik tiap orang (bukan setelan toko).
  const selNada = bg.querySelector<HTMLSelectElement>('#nt_nada');
  selNada?.addEventListener('change', () => { setNadaSaya(selNada.value); bunyikan(selNada.value); });
  bg.querySelector('#nt_coba')?.addEventListener('click', () => bunyikan(selNada?.value));
  bg.querySelector('#nt_close')?.addEventListener('click', () => bg.remove());
  bg.querySelector('#nt_read')?.addEventListener('click', async () => {
    try { await repo.markRead(list.filter((n) => !n.read).map((n) => n.id)); bg.remove(); refreshTopbarBell(); } catch (e) { toast((e as Error).message); }
  });
}

/** Segarkan badge lonceng di topbar dari mana saja. */
export function refreshTopbarBell(): void {
  const host = document.querySelector<HTMLElement>('#bellHost');
  if (host) void refreshBadge(host);
}
